import React, { useEffect, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { SiteHeader } from './header'
import { Spinner } from './posts'
import { QRCodeSVG } from 'qrcode.react'

interface PageSummary {
  slug: string;
  title: string;
}

interface Page extends PageSummary {
  body: string;
}

interface HiddenListProps {
  pages: PageSummary[];
  navigate: (to: string) => void;
}

function HiddenList({ pages, navigate }: HiddenListProps) {
  if (pages.length === 0) {
    return <p className="text-muted">Nothing here yet.</p>
  }
  return (
    <ul className="list-group list-group-flush">
      {pages.map((page) => (
        <li key={page.slug} className="list-group-item px-0">
          <a
            href={`/hidden/${page.slug}`}
            className="text-decoration-none fw-semibold"
            onClick={(e) => { e.preventDefault(); navigate(`/hidden/${page.slug}`); }}
          >
            {page.title}
          </a>
        </li>
      ))}
    </ul>
  )
}

function HiddenDetail({ page }: { page: Page }) {
  return (
    <div>
      <div className="d-flex justify-content-center my-3">
        <QRCodeSVG value={`${window.location.origin}/hidden/${page.slug}`} size={200} marginSize={4} />
      </div>
      <h2>{page.title}</h2>
      <hr />
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{page.body}</ReactMarkdown>
    </div>
  )
}

interface HiddenPageProps {
  path: string;
  navigate: (to: string) => void;
}

export function HiddenPage({ path, navigate }: HiddenPageProps) {
  const slug = path.split('/')[2] || null
  const [summaries, setSummaries] = useState<PageSummary[] | null>(null)
  const [page, setPage] = useState<Page | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setError(null)
    const target = slug === null ? '/hidden/index.json' : `/hidden/${slug}.json`
    fetch(target)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.json()
      })
      .then((data) => {
        if (slug === null) setSummaries(data as PageSummary[])
        else setPage(data as Page)
      })
      .catch((e) => setError(String(e)))
  }, [slug])

  let content: React.ReactNode
  if (error) {
    content = <div className="alert alert-danger">Failed to load: {error}</div>
  } else if (slug !== null) {
    content = page && page.slug === slug ? <HiddenDetail page={page} /> : <Spinner />
  } else if (summaries === null) {
    content = <Spinner />
  } else {
    content = <HiddenList pages={summaries} navigate={navigate} />
  }

  return (
    <div className="container-md">
      {slug === null && (
        <div>
          <SiteHeader title="Hidden" />
          <p className="text-center text-muted fst-italic">Oh no, you found me. How did you get in here? Anyway, since you&apos;re here...</p>
        </div>
      )}
      {content}
    </div>
  )
}
